import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@devhop/ui";
import { CalculatorIcon, ChevronDownIcon } from "lucide-react";
import { useMemo } from "react";
import {
  type AssignmentDTO,
  type EventShowDTO,
  ROLE_FEE_KIND_LABELS,
} from "../../domain/contracts";
import { compareAssignmentLineKeys } from "../../domain/services/assignment-line-key";
import { formatDateTimeLocal } from "@/shared/lib/date-time-performance";

function formatMoney(value: number) {
  return `${new Intl.NumberFormat("lo-LA").format(value)} ກີບ`;
}

function AssignmentBreakdownCard({
  assignment,
  eventShows,
}: {
  assignment: AssignmentDTO;
  eventShows?: EventShowDTO[];
}) {
  const lines = assignment.pricingBreakdown?.lines ?? [];
  const selectedShows = useMemo(() => {
    if (!assignment.eventShowIds?.length || !eventShows?.length) return [];
    const map = new Map(eventShows.map((show) => [show.id, show]));
    return assignment.eventShowIds
      .map((id) => map.get(id))
      .filter((show): show is EventShowDTO => show != null);
  }, [assignment.eventShowIds, eventShows]);

  return (
    <article className="overflow-hidden rounded-lg border bg-background">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/20 px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-sm">
            {ROLE_FEE_KIND_LABELS[assignment.roleFeeKind]}
          </span>
          <Badge variant="outline">{assignment.showCount} ໂຊ</Badge>
          {assignment.roleFeeKind === "music_arranger" ? (
            <Badge variant="secondary">
              {formatMoney(assignment.showBaseAmount)}
            </Badge>
          ) : null}
          {assignment.rehearsalDays > 0 ? (
            <Badge variant="secondary">{assignment.rehearsalDays} ວັນຊ້ອມ</Badge>
          ) : null}
        </div>
        <span className="font-semibold text-sm tabular-nums">
          {formatMoney(assignment.grossAmount)}
        </span>
      </div>

      {selectedShows.length > 0 ? (
        <div className="border-b bg-muted/10 px-3 py-2">
          <p className="mb-1 text-muted-foreground text-xs">ໂຊທີ່ເລືອກ</p>
          <ul className="space-y-1 text-xs">
            {selectedShows.map((show, index) => (
              <li key={show.id} className="flex flex-wrap gap-2">
                <span className="font-medium">ໂຊ {index + 1}</span>
                <span className="text-muted-foreground">
                  {formatDateTimeLocal(show.showDate)}
                </span>
                <span className="font-mono text-[10px] text-muted-foreground/80">
                  {show.id}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {lines.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/10">
                <th className="px-3 py-2 text-left font-medium text-muted-foreground text-xs">
                  ລາຍການ
                </th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground text-xs">
                  ຈຳນວນ
                </th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground text-xs">
                  ລາຄາ/ໜ່ວຍ
                </th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground text-xs">
                  ລວມ
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {lines.map((line, index) => (
                <tr key={`${line.label}-${line.quantity}-${line.unitAmount}-${index}`}>
                  <td className="px-3 py-2 text-foreground">
                    <div>{line.label}</div>
                    {line.showDate ? (
                      <div className="text-muted-foreground text-xs">
                        {formatDateTimeLocal(line.showDate)}
                      </div>
                    ) : null}
                    {line.eventShowId ? (
                      <div className="font-mono text-[10px] text-muted-foreground/80">
                        {line.eventShowId}
                      </div>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-muted-foreground tabular-nums">
                    {line.quantity}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-muted-foreground tabular-nums">
                    {formatMoney(line.unitAmount)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums">
                    {formatMoney(line.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t bg-muted/10">
                <td
                  colSpan={3}
                  className="px-3 py-2 text-right text-muted-foreground text-xs"
                >
                  ຄ່າໂຊ
                </td>
                <td className="px-3 py-2 text-right font-medium tabular-nums">
                  {formatMoney(assignment.showAmount)}
                </td>
              </tr>
              {assignment.rehearsalAmount > 0 ? (
                <tr className="bg-muted/10">
                  <td
                    colSpan={3}
                    className="px-3 py-2 text-right text-muted-foreground text-xs"
                  >
                    ຄ່າຊ້ອມ
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {formatMoney(assignment.rehearsalAmount)}
                  </td>
                </tr>
              ) : null}
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="grid gap-2 px-3 py-3 text-sm sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-md border bg-muted/10 px-3 py-2">
            <span className="text-muted-foreground">ຄ່າໂຊ</span>
            <span className="font-medium tabular-nums">
              {formatMoney(assignment.showAmount)}
            </span>
          </div>
          {assignment.rehearsalAmount > 0 ? (
            <div className="flex items-center justify-between rounded-md border bg-muted/10 px-3 py-2">
              <span className="text-muted-foreground">ຄ່າຊ້ອມ</span>
              <span className="font-medium tabular-nums">
                {formatMoney(assignment.rehearsalAmount)}
              </span>
            </div>
          ) : null}
        </div>
      )}
    </article>
  );
}

export function RoleFeePricingBreakdownPanel({
  assignments,
  eventShows,
}: {
  assignments: AssignmentDTO[];
  eventShows?: EventShowDTO[];
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, AssignmentDTO[]>();
    for (const assignment of assignments) {
      const list = map.get(assignment.performerId) ?? [];
      list.push(assignment);
      map.set(assignment.performerId, list);
    }

    return [...map.entries()]
      .map(([performerId, rows]) => ({
        performerId,
        performerName: rows[0]?.performerName ?? performerId,
        rows: [...rows].sort((a, b) =>
          compareAssignmentLineKeys(a.lineKey, b.lineKey),
        ),
        grossAmount: rows.reduce((sum, row) => sum + row.grossAmount, 0),
      }))
      .sort((a, b) => a.performerName.localeCompare(b.performerName));
  }, [assignments]);

  const totalGrossAmount = useMemo(
    () =>
      assignments.reduce((sum, assignment) => sum + assignment.grossAmount, 0),
    [assignments],
  );

  if (assignments.length === 0) return null;

  return (
    <Collapsible defaultOpen={false} className="group/collapsible">
      <div className="rounded-xl border bg-card">
        <CollapsibleTrigger className="w-full text-left">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-muted/30">
            <div className="flex min-w-0 items-center gap-2">
              <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <CalculatorIcon className="size-4" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm">ລາຍລະອຽດການຄິດ</p>
                <p className="text-muted-foreground text-xs">
                  {assignments.length} ບັນທັດ · {grouped.length} ຄົນ · ຈຳນວນໂຊຕໍ່ຄົນ
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 pl-6 sm:pl-0">
              <span className="text-muted-foreground text-xs">ລວມ</span>
              <span className="font-bold text-sm tabular-nums">
                {formatMoney(totalGrossAmount)}
              </span>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="space-y-4 border-t px-4 py-4">
            {grouped.map((group) => (
              <section key={group.performerId} className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-medium text-sm">{group.performerName}</p>
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {formatMoney(group.grossAmount)}
                  </span>
                </div>
                <div className="space-y-2">
                  {group.rows.map((assignment) => (
                    <AssignmentBreakdownCard
                      key={assignment.id}
                      assignment={assignment}
                      eventShows={eventShows}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
